/*
  bundle-add.js - NOTFORTOMORROW OS2.0 - shared "add a stack in one click".
  Vanilla, no deps. Delegated click handler on any [data-add-bundle] button.

  data-handles="handle-a,handle-b,..." (comma-separated product handles).
  For each handle it fetches /products/<handle>.js, picks the first AVAILABLE
  variant, then POSTs /cart/add.js with one items[] array (single request).
  On success it dispatches the events cart-drawer.js already listens for
  (cart:add-success -> refreshAndOpen) so the stack lands in the drawer; if the
  drawer is absent or anything fails it falls back to /cart.

  Honest: it adds the REAL products at their real price. Any saving is the live
  store discount (the active automatic 20% applies in cart/checkout, exactly as
  on the live site) - the button never fabricates a price.
*/
(function () {
  'use strict';

  /* Locale-aware root: on /de/ the rendered handles are the localized (German)
     ones, which only resolve under the locale prefix. A bare /products/ fetch
     from a localized page 404s -> zero items -> the customer lands on an empty
     cart. Read the root from the page URL first because Shopify.routes.root was
     measured as "/" on localized pages on 2026-09-28, so translated handles
     returned 404 and the button said sold out. */
  function pageLocaleRoot() {
    var loc = String((window.Shopify && window.Shopify.locale) || document.documentElement.lang || '').toLowerCase();
    var p = window.location.pathname.toLowerCase();
    if (loc && (p === '/' + loc || p.indexOf('/' + loc + '/') === 0)) return '/' + loc + '/';
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  var ROOT = pageLocaleRoot();

  /* Only a variant the customer can actually buy. The old fallback to
     variants[0] pushed a SOLD-OUT variant into the atomic /cart/add.js call,
     which Shopify rejects as a whole - one sold-out member silently killed the
     entire stack and dumped the customer on an empty cart. */
  function buyableVariantId(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    var available = product.variants.find(function (v) { return v.available; });
    return available ? available.id : null;
  }

  function sleep(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* One flaky response (throttle, connection blip) must not empty the whole
     stack add - a single silently dropped fetch used to send the customer to
     an empty cart. Retry each request a couple of times before giving up.
     retryOnNetworkError must stay false for the cart POST: a timed-out POST
     may already have landed server-side and a blind retry would double-add. */
  async function fetchRetry(url, opts, retryOnNetworkError) {
    var res = null;
    for (var attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await sleep(attempt === 1 ? 500 : 1200);
      try {
        res = await fetch(url, opts);
        if (res.ok) return res;
        if (res.status !== 429 && res.status !== 403 && res.status !== 503) return res;
      } catch (err) {
        if (!retryOnNetworkError) throw err;
      }
    }
    return res;
  }

  async function resolveItems(handles) {
    var items = [], skipped = [];
    for (var i = 0; i < handles.length; i++) {
      var handle = handles[i].trim();
      if (!handle) continue;
      try {
        var res = await fetchRetry(ROOT + 'products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' }
        }, true);
        if (!res || !res.ok) { skipped.push(handle); continue; }
        var product = await res.json();
        var id = buyableVariantId(product);
        if (id) { items.push({ id: id, quantity: 1 }); }
        else { skipped.push(product.title || handle); }
      } catch (_) { skipped.push(handle); }
    }
    return { items: items, skipped: skipped };
  }

  /* Say what happened, next to the button. Never leave the customer guessing
     and never send them to an empty cart without a word. */
  function note(btn, text) {
    var host = btn.parentNode;
    if (!host) return;
    var el = host.querySelector('[data-add-bundle-note]');
    if (!text) { if (el) el.textContent = ''; return; }
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-add-bundle-note', '');
      el.setAttribute('role', 'status');
      el.style.cssText = 'margin:8px 0 0;font-size:11px;line-height:1.4;letter-spacing:.06em;text-transform:uppercase;font-family:var(--f-mono,inherit);color:var(--ink-3,#52524C)';
      host.insertBefore(el, btn.nextSibling);
    }
    el.textContent = text;
  }

  // Write text to the dedicated label span when present (keeps the button's SVG icon
  // intact); otherwise fall back to the whole button's textContent.
  function setLabel(btn, text) {
    var span = btn.querySelector('[data-add-bundle-label]');
    if (span) { span.textContent = text; } else { btn.textContent = text; }
  }
  function readLabel(btn) {
    var span = btn.querySelector('[data-add-bundle-label]');
    return span ? span.textContent : btn.textContent;
  }

  function setState(btn, state) {
    var loading = btn.getAttribute('data-label-loading') || 'Adding...';
    var done = btn.getAttribute('data-label-done') || 'Added to cart';
    var idle = btn.getAttribute('data-label-idle');
    if (idle === null) {
      idle = btn.getAttribute('data-label-default') || readLabel(btn);
      btn.setAttribute('data-label-idle', idle);
    }
    btn.classList.remove('is-loading', 'is-done');
    if (state === 'loading') {
      btn.classList.add('is-loading'); btn.disabled = true;
      btn.setAttribute('aria-busy', 'true'); setLabel(btn, loading);
    } else if (state === 'done') {
      btn.classList.add('is-done'); btn.disabled = false;
      btn.removeAttribute('aria-busy'); setLabel(btn, done);
      setTimeout(function () {
        btn.classList.remove('is-done');
        setLabel(btn, btn.getAttribute('data-label-idle'));
      }, 2600);
    } else {
      btn.disabled = false; btn.removeAttribute('aria-busy');
      setLabel(btn, btn.getAttribute('data-label-idle'));
    }
  }

  async function addBundle(btn) {
    if (btn.disabled) return;
    var handles = (btn.getAttribute('data-handles') || '').split(',');
    if (!handles.length) return;
    setState(btn, 'loading');
    note(btn, '');
    try {
      var resolved = await resolveItems(handles);
      var items = resolved.items;
      var skipped = resolved.skipped;
      /* Every member sold out: say so and stay put. Redirecting to an empty
         cart is what made this failure invisible to customer and merchant. */
      if (!items.length) {
        setState(btn, 'idle');
        note(btn, btn.getAttribute('data-label-none') || 'Temporarily sold out');
        return;
      }
      var res = await fetchRetry(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }, false);
      if (!res || !res.ok) throw new Error('add-failed');
      setState(btn, 'done');
      /* Partial stack: the rest IS in the cart, so keep selling and be honest
         about the one that was not added. */
      note(btn, skipped.length
        ? (btn.getAttribute('data-label-notadded') || 'Sold out, not added') + ': ' + skipped.join(', ')
        : '');
      if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:add-success'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        window.location.href = ROOT + 'cart';
      }
    } catch (_) {
      setState(btn, 'idle');
      note(btn, btn.getAttribute('data-label-none') || 'Temporarily sold out');
    }
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-add-bundle]');
    if (!btn) return;
    e.preventDefault();
    addBundle(btn);
  });
})();
